// PX-Connect-Terminerfassung — Persistenz in `Projekt.metadata` (siehe ADR 0005).
//
// Ein einziger, wachsender Datensatz unter dem Schlüssel `pxConnectTermine`, der bei
// jedem Speichern vollständig überschrieben wird (merge-then-overwrite wie beim
// Schadenbericht-Draft `pxConnectDraft`). Es wird KEINE Korrespondenz-Zeile angelegt (ADR 0005).
import { isEqual } from 'lodash';
import { parseMetadata } from '~/meta/data/px-connect';
import { PxConnectOrtstermin } from './pxConnect';

export const PX_CONNECT_TERMINE_METADATA_KEY = 'pxConnectTermine';

export interface PxConnectTermineRecord {
    /**
     * Werte je Feldname aus `pxConnectTerminFields` — „YYYY-MM-DD" aus dem Formular oder ein
     * voller ISO-Zeitstempel aus der automatischen Terminspeicherung (UCPWO-387, „Zeitpunkt
     * der Speicherung"). Zum Anzeigen im Datumsfeld immer {@link toDateInputValue} benutzen.
     */
    values: Record<string, string>;
    /** Weitere Ortstermine (Datum + Kommentar), max. 2 — siehe PxConnectTerminBlock. */
    ortstermine: PxConnectOrtstermin[];
    savedAt: string;
}

export interface PxConnectTerminePersistedState {
    values: Record<string, boolean>;
    ortstermine: Partial<Record<keyof PxConnectOrtstermin, boolean>>[];
}

/**
 * Liest den gespeicherten Terminerfassungs-Datensatz aus `Projekt.metadata`.
 * Fehlend oder strukturell unbrauchbar → `undefined` (Formular startet leer).
 */
export const pxConnectTermineFromMetadata = (
    metadata: unknown
): PxConnectTermineRecord | undefined => {
    const record = parseMetadata(metadata)?.[PX_CONNECT_TERMINE_METADATA_KEY];
    if (!record || typeof record !== 'object') return undefined;

    const { values, ortstermine, savedAt } = record as Partial<PxConnectTermineRecord>;
    return {
        values: values && typeof values === 'object' ? values : {},
        ortstermine: Array.isArray(ortstermine)
            ? ortstermine.map((row) => ({
                  datum: typeof row?.datum === 'string' ? row.datum : '',
                  kommentar: typeof row?.kommentar === 'string' ? row.kommentar : '',
              }))
            : [],
        savedAt: typeof savedAt === 'string' ? savedAt : '',
    };
};

/**
 * Ein Wert aus `values` für ein `<input type="date">`.
 *
 * Ein voller ISO-Zeitstempel (automatische Terminspeicherung) wird vom Datumsfeld sonst
 * verworfen und das Feld erscheint leer — obwohl ein Wert gespeichert ist. Angezeigt wird
 * deshalb nur der Datumsteil; im State bleibt der Zeitstempel stehen, bis das Feld
 * tatsächlich bearbeitet wird.
 */
export const toDateInputValue = (value: unknown): string =>
    typeof value === 'string' ? value.slice(0, 10) : '';

/**
 * Baut den neuen `metadata`-JSON-String: bestehende Schlüssel (sourceSystem,
 * serviceRequestId, pxConnectDraft, …) bleiben erhalten, `pxConnectTermine` wird
 * vollständig ersetzt. AWSJSON-Variablen müssen als JSON-String gesendet werden.
 *
 * Ausnahme: Schlüssel INNERHALB des Datensatzes, die das Formular nicht verwaltet, werden
 * durchgereicht — `notPossible` und `reasons` schreibt die automatische Terminspeicherung
 * (UCPWO-387), und ein manuelles Speichern darf sie nicht wegräumen. Sie bleiben auch
 * stehen, wenn das zugehörige Datum von Hand geleert wird: für „nicht möglich" gibt es im
 * Formular noch keine Bedienung, also auch keine Möglichkeit, es bewusst zurückzunehmen.
 */
export const mergePxConnectTermineMetadata = (
    existingMetadata: unknown,
    record: Omit<PxConnectTermineRecord, 'savedAt'>
): string => {
    const metadata = parseMetadata(existingMetadata) || {};
    const existingRecord = metadata[PX_CONNECT_TERMINE_METADATA_KEY];

    return JSON.stringify({
        ...metadata,
        [PX_CONNECT_TERMINE_METADATA_KEY]: {
            ...(existingRecord && typeof existingRecord === 'object' ? existingRecord : {}),
            ...record,
            savedAt: new Date().toISOString(),
        },
    });
};

/**
 * Würde ein Speichern etwas anderes schreiben, als schon steht? Verglichen werden genau die
 * beiden Schlüssel, die das Formular verwaltet und `mergePxConnectTermineMetadata` ersetzt —
 * `savedAt` und die Maps der Automatik (`notPossible`, `reasons`) zählen bewusst nicht, die
 * bleiben beim Speichern ohnehin stehen. Kein Datensatz zählt als leeres Formular.
 */
export const hasPxConnectTermineChanges = (
    persistedRecord: PxConnectTermineRecord | undefined,
    currentValues: Record<string, string>,
    currentOrtstermine: PxConnectOrtstermin[]
): boolean =>
    !isEqual(persistedRecord?.values ?? {}, currentValues) ||
    !isEqual(persistedRecord?.ortstermine ?? [], currentOrtstermine);

const isPersistedString = (persisted: unknown, current: unknown): boolean =>
    typeof persisted === 'string' && persisted !== '' && persisted === current;

export const buildPxConnectTerminePersistedState = (
    persistedRecord: PxConnectTermineRecord | undefined,
    currentValues: Record<string, string>,
    currentOrtstermine: PxConnectOrtstermin[]
): PxConnectTerminePersistedState => ({
    values: Object.fromEntries(
        Object.entries(persistedRecord?.values || {}).map(([name, persistedValue]) => [
            name,
            isPersistedString(persistedValue, currentValues[name]),
        ])
    ),
    ortstermine: currentOrtstermine.map((row, index) => {
        const persistedRow = persistedRecord?.ortstermine[index];
        return {
            datum: isPersistedString(persistedRow?.datum, row.datum),
            kommentar: isPersistedString(persistedRow?.kommentar, row.kommentar),
        };
    }),
});

/**
 * Darf gespeichert werden? (PXC-B-23)
 *
 * Die Regel steht hier und nicht im Hook, damit sie prüfbar ist, ohne den halben
 * Dialog zu rendern — und weil sie eine Regel ist, keine Darstellung.
 *
 * **Ein ladendes Recht ist kein fehlendes.** Solange die Antwort unterwegs ist, wird
 * NICHT abgelehnt. Genau daran ist der Knopf im Projektüberblick schon einmal
 * gescheitert: er meldete während des Ladens „keine Berechtigung" und verschwand.
 * Eine Ablehnung beim Speichern wäre dieselbe Falle, nur teurer — der Benutzer hätte
 * dann schon getippt.
 */
export const darfTermineSchreiben = (rechtLaedt: boolean, hatRecht: boolean): boolean =>
    rechtLaedt || hatRecht;
